/**
 * Journeys helper (accounts).
 *
 * Subset of the canonical CODEDESIGN_JOURNEYS funnel-client-reference.js: ONLY the anonymous-id /
 * first-touch / signup-context functions. The accounts app sends NO `trackFunnel` events (the
 * backend derives signup/login server-side from the request body's `analytics` context), so
 * there is no API base, no network call and no APP_NAME here. Logic is copied unchanged.
 * Contract: CODEDESIGN_JOURNEYS_CONTRACTS.md §3.
 *
 * Hard rules this file exists to enforce:
 *   - never throws
 *   - disabled by localStorage.cd_funnel_off === '1'; no cd_vid cookie is created under GPC / DNT
 *   - no new dependencies
 */

const CD_VID_COOKIE = "cd_vid";
const CD_FT_COOKIE = "cd_ft";
const KILL_KEY = "cd_funnel_off";
const ALLOWED_COOKIE_DOMAIN = ".codedesign.ai";

// Kept for parity with the canonical reference (accounts has no JWT to send and no events to
// authenticate); intentionally unused here.
// eslint-disable-next-line no-unused-vars
function getAuthToken() {
  return "";
}

function privacyOptOut() {
  try {
    return navigator.globalPrivacyControl === true || navigator.doNotTrack === "1";
  } catch (_) {
    return false;
  }
}

function killed() {
  try {
    return window.localStorage.getItem(KILL_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function uuid() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
  } catch (_) {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : "";
  } catch (_) {
    return "";
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The anonymous visitor id, or '' when absent/invalid. */
export function getCdVid() {
  const v = readCookie(CD_VID_COOKIE);
  return UUID_RE.test(v) ? v : "";
}

/** Create the first-party anonymous id if missing (unless the visitor opted out of tracking). */
export function ensureCdVid() {
  try {
    if (killed() || privacyOptOut()) return "";
    const existing = getCdVid();
    if (existing) return existing;
    const id = uuid();
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    const host = window.location.hostname || "";
    const domain = /(^|\.)codedesign\.ai$/i.test(host) ? "; Domain=" + ALLOWED_COOKIE_DOMAIN : "";
    document.cookie = CD_VID_COOKIE + "=" + id + "; Path=/; Max-Age=31536000; SameSite=Lax" + secure + domain;
    return getCdVid();
  } catch (_) {
    return "";
  }
}

/** First-touch attribution from the cd_ft cookie (set by the landing page) or null. */
export function readFirstTouch() {
  try {
    const raw = readCookie(CD_FT_COOKIE);
    if (!raw) return null;
    const ft = JSON.parse(raw);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "ttclid", "referrer", "referring_domain", "landing_path"];
    const out = {};
    keys.forEach(function (k) {
      if (ft && typeof ft[k] === "string" && ft[k]) out[k] = ft[k].slice(0, 200);
    });
    return Object.keys(out).length ? out : null;
  } catch (_) {
    return null;
  }
}

/** Context for signup/login bodies (accounts app). Always safe to spread into a request body. */
export function readAnalyticsContext() {
  try {
    const ctx = {};
    const vid = getCdVid();
    if (vid) ctx.cdVid = vid;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) ctx.tz = String(tz).slice(0, 64);
    } catch (_) {
      /* ignore */
    }
    try {
      if (navigator.language) ctx.lang = String(navigator.language).slice(0, 16);
    } catch (_) {
      /* ignore */
    }
    const ft = readFirstTouch();
    if (ft) ctx.firstTouch = ft;
    return ctx;
  } catch (_) {
    return {};
  }
}
